import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { company } from '../data/content';
import { buildEstimateMessage, smsHrefFor, mailHrefFor } from '../utils/estimate';
import { thanksgivingPromo, isPromoActive } from '../data/promo';
import { CheckIcon, AlertIcon, PhoneIcon, MailIcon, MessageIcon, CameraIcon, PlusIcon } from './icons';
const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
interface PoolPhoto {
  file: File;
  url: string;
  id: string;
}
const ease = [0.23, 1, 0.32, 1] as const;
type ContactMethod = 'Call' | 'Text' | 'Email';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FormValues {
  name: string;
  phone: string;
  email: string;
  zip: string;
  service: string;
  timeline: string;
  contactMethod: ContactMethod;
  details: string;
}
type FieldErrors = Partial<Record<keyof FormValues, string>>;
const serviceOptions = ['Pool plaster / replaster', 'Complete remodel', 'Tile work', 'Coping', 'Spray deck repair / resurfacing', 'Not sure — need an assessment'];
const timelineOptions = ['As soon as possible', 'Within 1–3 months', 'This season', 'Just getting pricing'];
const contactMethods: ContactMethod[] = ['Call', 'Text', 'Email'];
const initialValues: FormValues = {
  name: '',
  phone: '',
  email: '',
  zip: '',
  service: serviceOptions[0],
  timeline: timelineOptions[0],
  contactMethod: 'Call',
  details: ''
};
function validate(values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = 'Please tell us your name.';
  const digits = values.phone.replace(/\D/g, '');
  if (!digits) errors.phone = 'We need a number to reach you.';else if (digits.length < 10) errors.phone = 'Enter a 10-digit phone number.';
  if (values.contactMethod === 'Email' || values.email.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
  }
  if (!values.zip.trim()) errors.zip = 'City or ZIP helps us schedule the visit.';
  return errors;
}
const inputClass = 'w-full rounded-md border bg-white px-3.5 py-3 text-[15px] text-navy-800 placeholder:text-navy-300 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-sky-400';
export function EstimateForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [photos, setPhotos] = useState<PoolPhoto[]>([]);
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  const [sharedWithPhotos, setSharedWithPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => () => {
    photos.forEach(photo => URL.revokeObjectURL(photo.url));
  }, [photos]);
  const update = <K extends keyof FormValues,>(key: K, value: FormValues[K]) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const addPhotos = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    const valid: PoolPhoto[] = [];
    let notice: string | null = null;
    incoming.forEach(file => {
      if (!file.type.startsWith('image/')) {
        notice = 'Only image files can be attached.';
        return;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        notice = `${file.name} is larger than 10 MB and was skipped.`;
        return;
      }
      valid.push({
        file,
        url: URL.createObjectURL(file),
        id: `${file.name}-${file.lastModified}`
      });
    });
    setPhotos(prev => {
      const merged = [...prev];
      valid.forEach(photo => {
        if (merged.length >= MAX_PHOTOS) {
          notice = `You can attach up to ${MAX_PHOTOS} photos.`;
          URL.revokeObjectURL(photo.url);
          return;
        }
        if (!merged.some(existing => existing.id === photo.id)) merged.push(photo);
      });
      return merged;
    });
    setPhotoNotice(notice);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removePhoto = (id: string) => {
    setPhotos(prev => {
      const target = prev.find(photo => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter(photo => photo.id !== id);
    });
    setPhotoNotice(null);
  };
  const message = buildEstimateMessage(values);
  const smsHref = smsHrefFor(message);
  const mailHref = mailHrefFor(company.email, values.name, message);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('submitting');
    const files = photos.map(photo => photo.file);
    const canShareFiles = files.length > 0 && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({
      files
    });
    if (canShareFiles) {
      try {
        await navigator.share({
          title: 'Free estimate request — A&J Pool Plaster',
          text: message,
          files
        });
        setSharedWithPhotos(true);
        setStatus('success');
        return;
      } catch (shareError) {
        if ((shareError as DOMException)?.name === 'AbortError') {
          setStatus('idle');
          return;
        }
      }
    }
    try {
      window.location.href = smsHref;
      window.setTimeout(() => setStatus('success'), 600);
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.25,
      ease
    }} role="status" className="rounded-lg bg-white p-7 sm:p-8" data-id="element-58" data-mp="2hp40fspmwzrr">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600" data-id="element-59" data-mp="3k2zpdvfjioy7">
          <CheckIcon className="h-6 w-6" data-id="element-60" data-mp="3f44wu1rrtm0w" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold text-navy-800" data-id="element-61" data-mp="3f4esa4vl4ldz">
          Almost done, {values.name.split(' ')[0]} — hit send.
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-navy-500" data-id="element-62" data-mp="363q96vrc3uu8">
          {sharedWithPhotos ? `Your details and ${photos.length} photo${photos.length === 1 ? '' : 's'} are queued in the app you picked. Press send and we'll reply by ${values.contactMethod.toLowerCase()} within one business day to schedule your free on-site estimate in ${values.zip}.` : `Your messaging app should have opened with everything filled in for ${company.phoneDisplay}. Press send and we'll reply by ${values.contactMethod.toLowerCase()} within one business day to schedule your free on-site estimate in ${values.zip}.`}
        </p>
        {photos.length > 0 && !sharedWithPhotos && <p className="mt-3 flex items-start gap-2 rounded-md bg-sky-50 p-3.5 text-[13px] leading-relaxed text-navy-600" data-id="element-63" data-mp="svpacpok6uub">
            <CameraIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" data-id="element-64" data-mp="xtqw5iddd89h" />
            Your {photos.length === 1 ? 'photo' : `${photos.length} photos`} couldn&apos;t be
            attached automatically on this device — tap the attachment button in the message and
            pick them from your gallery before sending.
          </p>}
        <dl className="mt-6 space-y-2 border-t border-navy-100 pt-5 text-sm" data-id="element-65" data-mp="f4kii9rfn6uj">
          <div className="flex justify-between gap-4" data-id="element-66" data-mp="j6p9t1uerre0">
            <dt className="text-navy-400" data-id="element-67" data-mp="j5w2nzrjezvc">Service</dt>
            <dd className="text-right font-medium text-navy-700" data-id="element-68" data-mp="nx5p6rhshn30">{values.service}</dd>
          </div>
          <div className="flex justify-between gap-4" data-id="element-69" data-mp="3nl5i4zy6cnkz">
            <dt className="text-navy-400" data-id="element-70" data-mp="3nknfnlkx5d4m">Timeline</dt>
            <dd className="text-right font-medium text-navy-700" data-id="element-71" data-mp="3rlzb47qh4skq">{values.timeline}</dd>
          </div>
          <div className="flex justify-between gap-4" data-id="element-72" data-mp="2kydcg3m6cuhy">
            <dt className="text-navy-400" data-id="element-73" data-mp="2l19cpylhvv9k">Phone</dt>
            <dd className="text-right font-medium text-navy-700" data-id="element-74" data-mp="2g154swynk8uy">{values.phone}</dd>
          </div>
        </dl>
        <p className="mt-6 text-sm font-semibold text-navy-700" data-id="element-75" data-mp="2uy45s1z013gg">
          Didn&apos;t open? Send it another way:
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row" data-id="element-76" data-mp="2q0cgzgtnhxgh">
          <a href={smsHref} className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-3 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700" data-id="element-77" data-mp="33wzfvp55yxp1">
            <MessageIcon className="h-4 w-4" data-id="element-78" data-mp="3dpon27tolpek" />
            Open text again
          </a>
          <a href={mailHref} className="inline-flex items-center justify-center gap-2 rounded-md border border-navy-200 px-4 py-3 text-sm font-semibold text-navy-700 transition-colors duration-150 ease-out hover:border-navy-400" data-id="element-79" data-mp="38pizp3ug2p2a">
            <MailIcon className="h-4 w-4" data-id="element-80" data-mp="56wevmf4x8tw" />
            Send by email
          </a>
          <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-md border border-navy-200 px-4 py-3 text-sm font-semibold text-navy-700 transition-colors duration-150 ease-out hover:border-navy-400" data-id="element-81" data-mp="a56a1x0v09fi">
            <PhoneIcon className="h-4 w-4" data-id="element-82" data-mp="3ikq06n68hbqc" />
            Call instead
          </a>
        </div>
        <button type="button" onClick={() => {
        setValues(initialValues);
        setStatus('idle');
      }} className="mt-6 block text-sm font-semibold text-navy-400 underline-offset-4 transition-colors duration-150 ease-out hover:text-navy-600 hover:underline" data-id="element-83" data-mp="3nbg2tsn9z0hc">
          Send another request
        </button>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-lg bg-white p-6 sm:p-8" aria-label="Free estimate request" data-id="element-84" data-mp="34kozb7pyy4av">
      <h3 className="font-display text-xl font-bold text-navy-800" data-id="element-85" data-mp="17ksf57hbuifm">Request your free estimate</h3>
      <p className="mt-1.5 text-sm text-navy-400" data-id="element-86" data-mp="1bpi7imcs1evs">
        Takes about 40 seconds. Your answers open a ready-to-send text to{' '}
        {company.phoneDisplay} — just press send.
      </p>

      {isPromoActive() && <p className="mt-4 flex items-start gap-2.5 rounded-md border border-gold bg-gold/15 p-3.5 text-[13px] leading-snug text-navy-700" data-id="element-87" data-mp="12pd38l2vqwlu">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-navy-800" data-id="element-88" data-mp="1vcfzt7nzwx9g" />
          <span data-id="element-89" data-mp="1gn1nprt1c1qd">
            <span className="font-bold text-navy-800" data-id="element-90" data-mp="1gnjqz8e47g7s">{thanksgivingPromo.occasion}:</span>{' '}
            {thanksgivingPromo.headline.toLowerCase()} {thanksgivingPromo.condition}.{' '}
            {thanksgivingPromo.deadlineLabel} — it&apos;s added to your request automatically.
          </span>
        </p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-91" data-mp="2e2pcxclbm9fx">
        <div className="sm:col-span-2" data-id="element-92" data-mp="2e48st232eih6">
          <label htmlFor="name" className="block text-sm font-semibold text-navy-700" data-id="element-93" data-mp="2mng1fd96twk">
            Full name <span className="text-ember" data-id="element-94" data-mp="3tujdl6o51noe">*</span>
          </label>
          <input id="name" name="name" type="text" autoComplete="name" value={values.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} placeholder="Marisol Reyes" className={`mt-1.5 ${inputClass} ${errors.name ? 'border-ember' : 'border-navy-200'}`} data-id="element-95" data-mp="bfqxdyr0rqwh" />
          {errors.name && <p id="name-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-96" data-mp="3juipz8j8klee">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-97" data-mp="143ehlcodofil" />
              {errors.name}
            </p>}
        </div>

        <div data-id="element-98" data-mp="z4tbiwmg31m8">
          <label htmlFor="phone" className="block text-sm font-semibold text-navy-700" data-id="element-99" data-mp="z7flf2wt5t7m">
            Phone <span className="text-ember" data-id="element-100" data-mp="3sx1ct3gr2hdf">*</span>
          </label>
          <input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} placeholder="(832) 555-0199" className={`mt-1.5 ${inputClass} ${errors.phone ? 'border-ember' : 'border-navy-200'}`} data-id="element-101" data-mp="1pypwo7y2x90" />
          {errors.phone && <p id="phone-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-102" data-mp="3nyrjkyel5qug">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-103" data-mp="2qjdq7hu10kii" />
              {errors.phone}
            </p>}
        </div>

        <div data-id="element-104" data-mp="2vgvp0febxjcs">
          <label htmlFor="email" className="block text-sm font-semibold text-navy-700" data-id="element-105" data-mp="2vgcgtqehe9by">
            Email
          </label>
          <input id="email" name="email" type="email" autoComplete="email" value={values.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} placeholder="you@email.com" className={`mt-1.5 ${inputClass} ${errors.email ? 'border-ember' : 'border-navy-200'}`} data-id="element-106" data-mp="2nwbktbucvn8r" />
          {errors.email && <p id="email-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-107" data-mp="1v8z2jdv8a1l9">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-108" data-mp="1qbp1t22e23i4" />
              {errors.email}
            </p>}
        </div>

        <div data-id="element-109" data-mp="1m8iya26nuqfc">
          <label htmlFor="zip" className="block text-sm font-semibold text-navy-700" data-id="element-110" data-mp="1m89565qbk35f">
            City or ZIP <span className="text-ember" data-id="element-111" data-mp="1har574ua4y9k">*</span>
          </label>
          <input id="zip" name="zip" type="text" autoComplete="postal-code" value={values.zip} onChange={e => update('zip', e.target.value)} aria-invalid={Boolean(errors.zip)} aria-describedby={errors.zip ? 'zip-error' : undefined} placeholder="Katy, 77494" className={`mt-1.5 ${inputClass} ${errors.zip ? 'border-ember' : 'border-navy-200'}`} data-id="element-112" data-mp="3hd9zbzfkwdqa" />
          {errors.zip && <p id="zip-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-113" data-mp="32o41ofwe2vki">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-114" data-mp="2oouw0a5t7jus" />
              {errors.zip}
            </p>}
        </div>

        <div data-id="element-115" data-mp="2tmwg8yez1p7u">
          <label htmlFor="service" className="block text-sm font-semibold text-navy-700" data-id="element-116" data-mp="2toqsloc6g90b">
            What do you need?
          </label>
          <select id="service" name="service" value={values.service} onChange={e => update('service', e.target.value)} className={`mt-1.5 border-navy-200 ${inputClass}`} data-id="element-117" data-mp="rvt2i09sbuj2">
            {serviceOptions.map(option => <option key={option} data-id="element-118" data-mp="1as1o4r1cfwl4">{option}</option>)}
          </select>
        </div>

        <div className="sm:col-span-2" data-id="element-119" data-mp="1609vmaymngsc">
          <label htmlFor="timeline" className="block text-sm font-semibold text-navy-700" data-id="element-120" data-mp="15z8kk6hm2uam">
            How soon?
          </label>
          <select id="timeline" name="timeline" value={values.timeline} onChange={e => update('timeline', e.target.value)} className={`mt-1.5 border-navy-200 ${inputClass}`} data-id="element-121" data-mp="1kriu79kicchm">
            {timelineOptions.map(option => <option key={option} data-id="element-122" data-mp="1pmy4jrjb3q9a">{option}</option>)}
          </select>
        </div>

        <fieldset className="sm:col-span-2" data-id="element-123" data-mp="3vjka0kgko4mc">
          <legend className="text-sm font-semibold text-navy-700" data-id="element-124" data-mp="3rgxaryhknhf4">Best way to reach you</legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-125" data-mp="1ugb4flg115p9">
            {contactMethods.map(method => {
            const isActive = values.contactMethod === method;
            return <label key={method} className={`cursor-pointer rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ease-out ${isActive ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-navy-200 text-navy-500 hover:border-navy-400'}`} data-id="element-126" data-mp="24d5wblhso7uk">
                  <input type="radio" name="contactMethod" value={method} checked={isActive} onChange={() => update('contactMethod', method)} className="sr-only" data-id="element-127" data-mp="2n3x5on1s7y8y" />
                  {method}
                </label>;
          })}
          </div>
        </fieldset>

        <div className="sm:col-span-2" data-id="element-128" data-mp="phe5g6bboxa4">
          <label htmlFor="details" className="block text-sm font-semibold text-navy-700" data-id="element-129" data-mp="pe9u8ozfx19y">
            Anything we should know? <span className="font-normal text-navy-300" data-id="element-130" data-mp="gfwoymu06tkw">(optional)</span>
          </label>
          <textarea id="details" name="details" rows={3} value={values.details} onChange={e => update('details', e.target.value)} placeholder="Approximate pool size, cracks, stains, last time it was plastered…" className={`mt-1.5 resize-y border-navy-200 ${inputClass}`} data-id="element-131" data-mp="bgtlv7xl2xfk" />
        </div>

        <div className="sm:col-span-2" data-id="element-132" data-mp="zcpk6omqe4hv">
          <p className="text-sm font-semibold text-navy-700" data-id="element-133" data-mp="ug7la5e310dg">
            Photos of your pool{' '}
            <span className="font-normal text-navy-300" data-id="element-134" data-mp="1rvlhrutxumtm">(optional, up to {MAX_PHOTOS})</span>
          </p>
          <p className="mt-1 text-[13px] leading-snug text-navy-400" data-id="element-135" data-mp="1n43cwvoso23d">
            A couple of pictures of the cracks, staining, or tile line let us quote far more
            accurately before we ever visit.
          </p>

          <input ref={fileInputRef} id="photos" name="photos" type="file" accept="image/*" multiple onChange={e => addPhotos(e.target.files)} className="sr-only" data-id="element-136" data-mp="108a7zxgdnh3g" />

          <div className="mt-3 flex flex-wrap gap-3" data-id="element-137" data-mp="1e71lbhnf0yqj">
            {photos.map(photo => <div key={photo.id} className="relative h-24 w-24 overflow-hidden rounded-md border border-navy-200" data-id="element-138" data-mp="1j6wbtk3a3mb2">
                <img src={photo.url} alt={`Pool photo: ${photo.file.name}`} className="h-full w-full object-cover" data-id="element-139" data-mp="158xzek8ipzy7" />
                <button type="button" onClick={() => removePhoto(photo.id)} aria-label={`Remove ${photo.file.name}`} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-navy-900/80 text-white transition-colors duration-150 ease-out hover:bg-navy-900" data-id="element-140" data-mp="3pyx5eo605yy6">
                  <PlusIcon className="h-3.5 w-3.5 rotate-45" data-id="element-141" data-mp="76jvonx8qux" />
                </button>
              </div>)}

            {photos.length < MAX_PHOTOS && <label htmlFor="photos" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-navy-300 text-navy-500 transition-colors duration-150 ease-out hover:border-sky-500 hover:text-sky-600 focus-within:ring-2 focus-within:ring-sky-400" data-id="element-142" data-mp="33nnf3n7153bi">
                <CameraIcon className="h-6 w-6" data-id="element-143" data-mp="38louny58yki8" />
                <span className="text-[12px] font-semibold" data-id="element-144" data-mp="2kwvz42sjeeo5">
                  {photos.length === 0 ? 'Add photos' : 'Add more'}
                </span>
              </label>}
          </div>

          {photoNotice && <p className="mt-2 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-145" data-mp="1cey12w1n7qjr">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-146" data-mp="17h4sjwcvakgb" />
              {photoNotice}
            </p>}
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-147" data-mp="1m6ardj7sm8no">
        {status === 'error' && <motion.p initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} role="alert" className="mt-5 flex items-start gap-2 rounded-md border border-ember/40 bg-ember/5 p-3.5 text-[13px] leading-relaxed text-navy-700" data-id="element-148" data-mp="1hcg8uj3xbzq6">
            <AlertIcon className="mt-0.5 h-4 w-4 shrink-0 text-ember" data-id="element-149" data-mp="205agerz7wpkd" />
            We couldn&apos;t open your messaging app. Please{' '}
            <a href={mailHref} className="font-semibold text-sky-600 underline" data-id="element-150" data-mp="arf273bisosv">
              send it by email
            </a>{' '}
            or call {company.phoneDisplay} — we&apos;ll take the details over the phone.
          </motion.p>}
      </AnimatePresence>

      <button type="submit" disabled={status === 'submitting'} className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-md bg-navy-800 px-6 py-4 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500" data-id="element-151" data-mp="2mc4q67d7s9dn">
        {status === 'submitting' ? <>
            <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-152" data-mp="1oqqkxddji49e" />
            Opening your messages…
          </> : <>
            <MessageIcon className="h-5 w-5" data-id="element-153" data-mp="3o1rni2igvhxf" />
            {photos.length > 0 ? 'Send my request with photos' : 'Text my request to A&J'}
          </>}
      </button>

      <p className="mt-3 text-center text-[13px] text-navy-400" data-id="element-154" data-mp="3mihx5kihhk4g">
        Prefer email?{' '}
        <a href={mailHref} className="font-semibold text-sky-600 underline-offset-2 hover:underline" data-id="element-155" data-mp="3hnujyhsxkc56">
          Send the same details to {company.email}
        </a>
        . No obligation — we never share your information.
      </p>
    </form>;
}